import { StateCreator } from 'zustand'
import { SharedSlice } from '../../../slice'
import { PayClassification } from '$models'
import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import {
  payClassificationService,
  activatedPayClassificationService,
} from '$services'

interface ComplianceSharedState {
  payClassifications: PayClassification[]
}

export interface ComplianceSharedSlice extends ComplianceSharedState {
  /**
   * Get Pay Classifications list
   */
  getPayClassifications: () => Promise<PayClassification[]>

  /**
   * Activate a Pay Classification for the current venue and merge the returned model into the list.
   */
  activatePayClassification: (
    payClassificationId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Deactivate a Pay Classification for the current venue and merge the returned model into the list.
   */
  deactivatePayClassification: (
    payClassificationId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Resets PayClassifications Slice state
   */
  resetComplianceSharedSlice: () => void
}

const initialState: ComplianceSharedState = {
  payClassifications: [],
}

/**
 * Replace the matching Pay Classification with an updated copy (e.g. after activate/deactivate
 * returns the refreshed model), leaving the rest of the list untouched.
 */
const replacePayClassificationInArray = (
  payClassifications: PayClassification[],
  updated: PayClassification
): PayClassification[] =>
  payClassifications.map((payClassification) =>
    payClassification.id === updated.id ? updated : payClassification
  )

const createPayClassificationsSlice: StateCreator<
  ComplianceSharedSlice & SharedSlice,
  [],
  [],
  ComplianceSharedSlice
> = (set) => ({
  ...initialState,

  resetComplianceSharedSlice: () => set(initialState),

  getPayClassifications: async (): Promise<PayClassification[]> => {
    try {
      const response = await payClassificationService.getAll()
      set(() => ({
        payClassifications: response,
      }))
      return response
    } catch (error) {
      set(() => ({
        payClassifications: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve pay classifications')
      return []
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  activatePayClassification: async (
    payClassificationId: string,
    successMessage: string = 'Pay Classification activated',
    errorMessage: string = 'Failed to activate the Pay Classification'
  ): Promise<void> => {
    try {
      set(() => ({ isLoading: true }))
      const updated = await activatedPayClassificationService.activate(
        payClassificationId
      )
      set((state) => ({
        payClassifications: replacePayClassificationInArray(
          state.payClassifications,
          updated
        ),
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  deactivatePayClassification: async (
    payClassificationId: string,
    successMessage: string = 'Pay Classification deactivated',
    errorMessage: string = 'Failed to deactivate the Pay Classification'
  ): Promise<void> => {
    try {
      set(() => ({ isLoading: true }))
      const updated = await activatedPayClassificationService.deactivate(
        payClassificationId
      )
      set((state) => ({
        payClassifications: replacePayClassificationInArray(
          state.payClassifications,
          updated
        ),
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createPayClassificationsSlice
